import React, { useEffect, useState } from 'react';

import {
  Flex, Text, FlexProps, useToast,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';

import { mutateRoute } from 'api/routes';
import { Button, Toggle } from 'components';
import { ContentRoutes } from 'enums/routes';
import { GeneralStatus } from 'enums/status';
import resolver from 'validations/routes';

import ClientForm from './Client';
import InfoForm from './Info';
import { handleDefaultValues, parseFormData } from './utils';

interface RouteFormProps extends FlexProps {
  onCancel?: () => void
  defaultValues?: any
  onSuccess?: (_data: any) => void
}

export default function RouteForm({
  onCancel,
  onSuccess = (_:any) => null,
  defaultValues,
  ...rest
}: RouteFormProps) {
  const toast = useToast();
  const form = useForm({ resolver });
  const [isParsing, setIsParsing] = useState(false);
  const { id } = useParams<GeneralRoute>();
  const { goBack, push } = useHistory();
  const {
    register, handleSubmit, setError,
  } = form;
  const { mutate, isLoading } = useMutation((data: any) => mutateRoute({ data, id }), {
    onSuccess: ({ data }) => {
      onSuccess(data);
      push(`/${ContentRoutes.Routes}/${data.id}`);
      onCancel();
    },
    onError: ({ errors }) => {
      if (Array.isArray(errors)) {
        errors.forEach((error) => {
          setError(error.field, {
            type: error.rule,
            message: error.message,
          });
        });
      } else {
        toast({
          title: 'Ocorreu um erro.',
          description: errors.message,
          status: 'error',
          duration: 5000,
          position: 'top-right',
          isClosable: true,
        });
      }
    },
  });

  useEffect(() => {
    if (defaultValues) {
      handleDefaultValues({ ...form, defaultValues });
    }
  }, []);

  const onSubmit = (data) => {
    setIsParsing(true);
    const params = parseFormData(data);
    mutate(params);
    setIsParsing(false);
  };

  return (
    <Flex
      mt={8}
      px={92}
      py={13}
      border="1px"
      borderColor="gray.200"
      borderRadius={2}
      flexDir="column"
      {...rest}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ClientForm {...form} />
          <InfoForm {...form} />
          <Toggle
            size="md"
            defaultChecked={defaultValues?.status === GeneralStatus.Active}
            {...register('active')}>
            ATIVA
          </Toggle>
          <Flex justify="flex-end">
            <Button
              onClick={onCancel ?? goBack}
              variant="secondary.teal"
              mr={2}>
              <Text variant="smallBold">Cancelar</Text>
            </Button>
            <Button
              isLoading={isLoading || isParsing}
              variant="primary.blue"
              type="submit">
              <Text variant="smallBold">Salvar</Text>
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
}
